/**
 * Session and day-boundary rules. The first module ported in Phase 1, chosen
 * because everything downstream re-buckets on it: change the day boundary and
 * the calendar, Day Overview, streaks and every per-day aggregate move with it.
 *
 * PORTED FROM `tradecoach-saas/app/index.html` — semantics, not code
 * (REBUILD_DECISIONS.md D3). Where the old functions read
 * `state.settings.sessionTimezone` from a global, these take it as a parameter:
 * `domain/` is pure, which is what lets the same code run in a Deno Edge
 * Function.
 *
 * ⚠ The gate is EXACT MATCH (D21). These must agree with the old client to the
 * character on real data — 603 trades, 88 of which cross the 18:00 ET boundary.
 */
import type { DayBoundaryMode, IsoDate } from './types';

export const DEFAULT_TZ = 'America/New_York';

/** CME session windows, ET-anchored. `''` is the maintenance gap, not an error. */
export type SessionLabel = 'Asia' | 'EU' | 'NY-AM' | 'NY-PM' | '';

// ---------------------------------------------------------------------------
// Date primitives
// ---------------------------------------------------------------------------
// `new Date(s + 'T00:00:00')` parses as LOCAL midnight, not UTC. That is
// deliberate and load-bearing: these dates are calendar labels, and appending
// 'Z' would shift them a day for anyone west of Greenwich.

const pad = (n: number): string => String(n).padStart(2, '0');

export function isoToDate(s: string): Date {
  return new Date(s + 'T00:00:00');
}

export function dateToISO(d: Date): IsoDate {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` as IsoDate;
}

// ---------------------------------------------------------------------------
// Timezone conversion
// ---------------------------------------------------------------------------

/**
 * A local clock time in `ianaTz` → UTC milliseconds, DST-correct.
 *
 * The trick: treat the local string as if it were UTC, render THAT instant in
 * the target zone, and the difference is the zone's offset at that moment.
 * Handles DST transitions without a table.
 */
export function localToUtcMs(
  dateStr: string | null,
  timeStr: string | null,
  ianaTz: string,
): number | null {
  if (!dateStr || !timeStr) return null;
  const tm = String(timeStr).trim();
  const hms = tm.length === 5 ? `${tm}:00` : tm;
  const fauxUtc = new Date(`${dateStr}T${hms}Z`);
  if (isNaN(fauxUtc.getTime())) return null;

  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: ianaTz,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
  const parts: Record<string, string> = {};
  for (const p of fmt.formatToParts(fauxUtc)) parts[p.type] = p.value;

  const tzAsUtc = Date.UTC(
    Number(parts['year']), Number(parts['month']) - 1, Number(parts['day']),
    Number(parts['hour']) % 24, Number(parts['minute']), Number(parts['second']),
  );
  return fauxUtc.getTime() - (tzAsUtc - fauxUtc.getTime());
}

interface EtParts { iso: string; hour: number; min: number }

/** A (date, clock) in `tz` → the equivalent ET calendar date and clock. */
export function toEtParts(dateStr: string, timeStr: string | null, tz: string): EtParts | null {
  const ms = localToUtcMs(dateStr, timeStr || '00:00', tz);
  if (ms == null) return null;
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: DEFAULT_TZ,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
  const p: Record<string, string> = {};
  for (const x of fmt.formatToParts(new Date(ms))) p[x.type] = x.value;
  const hour = Number(p['hour']) % 24;
  const min = Number(p['minute']);
  if (isNaN(hour) || isNaN(min)) return null;
  return { iso: `${p['year']}-${p['month']}-${p['day']}`, hour, min };
}

/**
 * Reinterpret a session-tz clock as an ET clock.
 *
 * ET callers and date-less callers are a NO-OP by design, so this is
 * byte-identical to the pre-timezone-feature behavior for the entire existing
 * ET user base. Preserve that short-circuit exactly — it is why the timezone
 * feature shipped without re-bucketing everyone's history.
 */
export function clockToEt(
  timeStr: string | null,
  dateStr: string | null,
  tz: string = DEFAULT_TZ,
): string | null {
  if (tz === DEFAULT_TZ || !dateStr || !timeStr) return timeStr;
  const et = toEtParts(dateStr, timeStr, tz);
  if (!et) return timeStr;
  return `${pad(et.hour)}:${pad(et.min)}`;
}

// ---------------------------------------------------------------------------
// Session bucketing
// ---------------------------------------------------------------------------

/**
 * Pure ET-clock bucketing. Boundaries are exact and asymmetric on purpose:
 * `>= 18` opens Asia, `<= 17` closes NY-PM, and 17:00–18:00 falls through to
 * `''` — the CME maintenance gap. A trade there has no session, which is not
 * the same as an error.
 */
export function sessionFromTimeRaw(timeStr: string | null): SessionLabel {
  if (!timeStr) return '';
  const m = String(timeStr).match(/^(\d{1,2}):(\d{2})/);
  if (!m) return '';
  const h = parseInt(m[1] as string, 10);
  const min = parseInt(m[2] as string, 10);
  if (isNaN(h) || isNaN(min) || h < 0 || h > 23 || min < 0 || min > 59) return '';
  const t = h + min / 60;
  if (t >= 18 || t < 3) return 'Asia';
  if (t < 9.5) return 'EU';
  if (t < 12) return 'NY-AM';
  if (t <= 17) return 'NY-PM';
  return '';
}

export function sessionFromTime(
  timeStr: string | null,
  dateStr: string | null,
  tz: string = DEFAULT_TZ,
): SessionLabel {
  return sessionFromTimeRaw(clockToEt(timeStr, dateStr, tz));
}

// ---------------------------------------------------------------------------
// Session day
// ---------------------------------------------------------------------------

/**
 * The session day a (date, clock) belongs to.
 *
 * CME sessions run 18:00 ET → 17:00 ET next day, so a Monday 18:30 trade
 * belongs to TUESDAY's session. `hour >= 18` rolls the date forward by one.
 *
 * Note the fallback: if `toEtParts` fails for a non-ET zone, it still tries to
 * read the raw hour rather than giving up — a wrong-but-plausible bucket beats
 * dropping the trade out of every aggregate.
 */
export function sessionDateFor(
  dateISO: string | null,
  timeStr: string | null,
  tz: string = DEFAULT_TZ,
): IsoDate | '' {
  if (!dateISO) return '';
  let etIso = dateISO;
  let hour = 0;

  if (tz === DEFAULT_TZ || !timeStr) {
    if (timeStr) {
      const m = String(timeStr).match(/^(\d{1,2}):(\d{2})/);
      if (m) {
        const h = parseInt(m[1] as string, 10);
        if (!isNaN(h) && h >= 0 && h <= 23) hour = h;
      }
    }
  } else {
    const et = toEtParts(dateISO, timeStr, tz);
    if (et) {
      etIso = et.iso;
      hour = et.hour;
    } else {
      const m = String(timeStr).match(/^(\d{1,2}):(\d{2})/);
      if (m) {
        const h = parseInt(m[1] as string, 10);
        if (!isNaN(h)) hour = h;
      }
    }
  }

  if (hour < 18) return etIso as IsoDate;
  const d = isoToDate(etIso);
  d.setDate(d.getDate() + 1);
  return dateToISO(d);
}

/**
 * The session day that IS today, right now.
 *
 * The anchor for the three relative range presets. `rangeBounds` takes `today`
 * as a parameter and does not know the clock; this is what a caller passes it
 * when the day-boundary mode is `session`.
 *
 * ⚠ ET WALL CLOCK, not the user's session timezone and not the browser's.
 * The CME session day is defined in New York regardless of where the trader
 * sits, and the old client's first version read `now.getHours()` — which was
 * browser-local and so broke for anyone not physically in their trading zone.
 *
 * Pure: `now` is a parameter. Reading `new Date()` here would put the clock
 * inside `domain/` and make the rollover untestable — the one hour a day that
 * matters most.
 */
export function currentSessionDate(now: Date): IsoDate {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: DEFAULT_TZ,
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hour12: false,
  });
  const p: Record<string, string> = {};
  for (const x of fmt.formatToParts(now)) p[x.type] = x.value;
  const etIso = `${p['year']}-${p['month']}-${p['day']}`;
  // `% 24` because en-US hour12:false renders midnight as "24" on some ICU
  // versions. Without it, 00:xx ET would read as hour 24 and roll the date.
  const hour = Number(p['hour']) % 24;
  if (hour < 18) return etIso as IsoDate;
  const d = isoToDate(etIso);
  d.setDate(d.getDate() + 1);
  return dateToISO(d);
}

/** Minimal shape `effectiveTradeDate` needs — deliberately not the full Trade. */
export interface DatedTrade {
  date: string | null;
  sessionDate: string | null;
}

/**
 * The date a trade counts toward. **Every aggregation and filter must call this
 * rather than reading `date` directly** — that is the entire day-boundary
 * feature, and a surface that reads the raw field silently opts out of it.
 *
 * In session mode `sessionDate` is precomputed at import; the fallback to
 * `date` covers trades that predate the feature.
 */
export function effectiveTradeDate(
  t: DatedTrade | null | undefined,
  mode: DayBoundaryMode,
): IsoDate | '' {
  if (mode === 'session') return ((t && (t.sessionDate || t.date)) || '') as IsoDate | '';
  return ((t && t.date) || '') as IsoDate | '';
}

/** What `tradeDayFor` needs on top of `DatedTrade`: the clock time. */
export interface TimedTrade extends DatedTrade {
  timeIn: string | null;
}

/**
 * The day a trade counts toward, **computed from its own date and entry time**
 * rather than read from the stored column.
 *
 * ── 🔴 WHY THIS EXISTS ALONGSIDE `effectiveTradeDate` (TCR-96) ──
 *
 * `effectiveTradeDate` reads `sessionDate`, and its docstring says that column
 * "is precomputed at import". It is not reliably: `selectors/derive.ts` has
 * recomputed it for every row since the day after this filter was written,
 * because — in its own words — *"the stored column is stale rather than absent
 * on the rows that matter: a 22:36 trade whose `session_date` says the same
 * calendar day."*
 *
 * 🔴 THE HEAL RAN TOO LATE TO HELP. Membership in a day was decided by
 * `loadTradesInRange` on the RAW row, and only the survivors reached `deriveRow`
 * — so a trade with a missing or stale column was dropped before anything could
 * correct it. Josh took two trades at 21:26 on 2026-10-25 and they did not
 * appear on the session they belonged to. Measured on the live book at the time:
 * 14 rows with no session date, and 20 evening rows carrying a stale one, the
 * oldest from 2026-06-30.
 *
 * ▶ **USE THIS ANYWHERE MEMBERSHIP IS DECIDED ON AN UNDERIVED ROW.** Use
 * `effectiveTradeDate` on rows that have been through `deriveRow`, where the
 * column has already been corrected — which is every other caller in the app.
 *
 * ⚠ `tz` IS REQUIRED, DELIBERATELY. `sessionDateFor` defaults it, and a caller
 * taking that default while `deriveRow` passes the user's `sessionTz` would put
 * a trade in a day and then leave it out of that day's totals. There is no
 * sensible default here; the caller knows the setting or should not be asking.
 */
export function tradeDayFor(
  t: TimedTrade | null | undefined,
  mode: DayBoundaryMode,
  tz: string,
): IsoDate | '' {
  if (mode !== 'session') return ((t && t.date) || '') as IsoDate | '';
  // ⚠ Falls back to the raw date only when the computation yields nothing —
  // a row with no date at all. It never falls back to the STORED column, which
  // is the value this function exists not to trust.
  const computed = t ? sessionDateFor(t.date, t.timeIn, tz) : '';
  return (computed || (t && t.date) || '') as IsoDate | '';
}

// ---------------------------------------------------------------------------
// Calendar navigation
// ---------------------------------------------------------------------------
// CME has no Saturday or Sunday session, so stepping skips them.

export function isWeekendIso(iso: string | null): boolean {
  if (!iso) return false;
  const dow = isoToDate(iso).getDay();
  return dow === 0 || dow === 6;
}

export function previousSessionDate(sessionDateISO: string): IsoDate {
  const d = isoToDate(sessionDateISO);
  d.setDate(d.getDate() - 1);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() - 1);
  return dateToISO(d);
}

export function nextSessionDate(sessionDateISO: string): IsoDate {
  const d = isoToDate(sessionDateISO);
  d.setDate(d.getDate() + 1);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  return dateToISO(d);
}

const TZ_LABELS: Record<string, string> = {
  'America/New_York': 'ET', 'America/Chicago': 'CT', 'America/Denver': 'MT',
  'America/Phoenix': 'MST', 'America/Los_Angeles': 'PT', 'America/Anchorage': 'AKT',
};

/** Short axis-label form of a timezone. Display-only; never a keying value. */
export function sessionTzLabel(tz: string = DEFAULT_TZ, now: Date = new Date()): string {
  const known = TZ_LABELS[tz];
  if (known) return known;
  try {
    return (
      new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
        .formatToParts(now)
        .find((p) => p.type === 'timeZoneName')?.value || ''
    );
  } catch {
    return '';
  }
}
